//@flow
import * as Immutable from 'immutable';
import { MasterBagModel } from 'common/models/flow/MasterBagModel';
import type { MasterBagModelModelType } from 'common/models/flow/MasterBagModel';
import { SkillId } from 'common/models/internal/SkillId';
import { TransporterId } from 'common/models/internal/TransporterId';
import { Volume } from 'common/models/internal/Volume';
import { Dimensions } from 'common/models/internal/Dimensions';
import I18nHelper from 'common/helpers/I18nHelper';
import EntityTypeEnum from 'common/enums/EntityTypeEnum';
import {
    validatable,
    validateAll,
    softValidateAll,
    validateId
} from 'common/utils/ValidationUtil';
import { DoorDimensions } from 'common/models/internal/DoorDimensions';
import { AuditMetadata } from 'common/models/internal/AuditMetadata';
import DataUtils from 'common/utils/DataUtils';
import RegExUtil from 'common/utils/RegExUtil';
import { RiderPersonaLimits } from './RiderPersonaLimits';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';

const translationsNamespace = ['entityManagement', 'validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);
const ENTITY_NAME_ID = 'masterBagModel';

export const LoadingUnloadingSideEnum = new EntityTypeEnum({
    REAR: 'REAR',
    RIGHT: 'RIGHT',
    LEFT: 'LEFT',
    TOP: 'TOP'
});

export const BagTypeEnum = new EntityTypeEnum({
    CONTAINER: 'CONTAINER',
    PALLET: 'PALLET'
});

export const TransportModeEnum = new EntityTypeEnum({
    ROAD: 'ROAD',
    RAIL: 'RAIL',
    SEA: 'SEA',
    AIR: 'AIR'
});

const VolumeUnitType = {
    ItemCount: 'ITEM_COUNT',
    MetersCubic: 'METERS_CUBIC',
    LBHMeters: 'LBH_METERS'
};

@validatable
class InternalMasterBagModel extends MasterBagModel {
    static fromJS(json: MasterBagModelModelType): InternalMasterBagModel {
        const state: Object = Object.assign({}, json);
        state.skills = state.skills
            ? Immutable.List(state.skills).map(item => SkillId.fromJS(item))
            : state.skills;
        state.transporterId = state.transporterId
            ? TransporterId.fromJS(state.transporterId)
            : state.transporterId;
        state.volume = Volume.fromJS(state.volume);
        state.usableVolume = Volume.fromJS(state.usableVolume);
        state.dimensions = Dimensions.fromJS(state.dimensions);
        state.usableDimensions = Dimensions.fromJS(state.usableDimensions);
        state.pieceDimensionLimit = state.pieceDimensionLimit
            ? Dimensions.fromJS(state.pieceDimensionLimit)
            : state.pieceDimensionLimit;
        state.doorDimensions = state.doorDimensions
            ? DoorDimensions.fromJS(state.doorDimensions)
            : state.doorDimensions;
        state.limits = state.limits
            ? RiderPersonaLimits.fromJS(state.limits)
            : state.limits;
        state.transportModes = Immutable.List(state.transportModes);
        state.auditMetadata = AuditMetadata.fromJS(state.auditMetadata);
        state.customProperties = Immutable.Map(state.customProperties);
        return new this(Immutable.Map(state));
    }

    get skills(): ?Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    get transporterId(): ?TransporterId {
        return this._state.get('transporterId');
    }

    get volume(): Volume {
        return this._state.get('volume');
    }

    get usableVolume(): Volume {
        return this._state.get('usableVolume');
    }

    get dimensions(): Dimensions {
        return this._state.get('dimensions');
    }

    get usableDimensions(): Dimensions {
        return this._state.get('usableDimensions');
    }

    static defaultInit(): InternalMasterBagModel {
        const volume = {
            unit: VolumeUnitType.MetersCubic,
            value: null
        };

        const dimensions = Dimensions.fromJS({
            height: '',
            breadth: '',
            length: '',
            unit: 'm'
        });

        const limits = RiderPersonaLimits.defaultInit();

        return InternalMasterBagModel.fromJS({
            volume,
            type: 'CONTAINER',
            usableVolume: volume,
            dimensions,
            usableDimensions: dimensions,
            limits
        });
    }

    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    get pieceDimensionLimit(): ?Dimensions {
        return this._state.get('pieceDimensionLimit');
    }

    get doorDimensions(): ?DoorDimensions {
        return this._state.get('doorDimensions');
    }

    get auditMetadata(): AuditMetadata {
        return this._state.get('auditMetadata');
    }

    validateId() {
        return validateId(this.id);
    }

    validateName() {
        if (
            DataUtils.isEmpty(this.name) ||
            DataUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return this.validateId() || this.validateName();
    }

    defaultSanitize(): this {
        const sanitizedBagModel = this.sanitize();
        return sanitizedBagModel;
    }

    sanitize(): this {
        let sanitizedEntity = this;
        const { transportModes } = this;
        const sanitizedTransportModes = transportModes.map(
            mode => mode.value ?? mode
        );
        sanitizedEntity = sanitizedEntity.setTransportModes(
            sanitizedTransportModes
        );

        return sanitizedEntity;
    }

    get limits(): ?RiderPersonaLimits {
        return this._state.get('limits');
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }
}

export { InternalMasterBagModel as MasterBagModel };
